import type { RootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import { ROOT_MODAL_DRAWER_PROP, ROOT_MODAL_PRESENTATION_PROP } from 'components/Drawers/standardDrawerContract';
import type { RootModalDrawerOptions, StandardDrawerPlane } from 'components/Drawers/standardDrawerContract';
import type { RecordOf } from 'immutable';

import { getRegisteredRootModalDrawerOptions, getRegisteredRootModalPresentation } from './modalRegistry';

type RootModalRecord = RecordOf<RootUiData>;
type ModalProps = Record<string, unknown>;
type RootModalPresentation = 'modal' | string | undefined;

type RootModalDrawerPresentationOptions = {
  drawerOptions: RootModalDrawerOptions | null;
  plane: StandardDrawerPlane;
  underlyingDrawerOptions: RootModalDrawerOptions | null;
};

type DrawerPresentationOptionsInput = {
  drawerEntries?: RootModalDrawerOptions[];
  hasUnderlyingModal?: boolean;
  index: number;
  modalArray: RootModalRecord[];
  webDrawerInput: boolean;
};

type ImmutableLikeValue = {
  toJS?: () => unknown;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getModalProps = (modal: RootModalRecord): ModalProps => {
  const modalProps: ModalProps = {};
  modal.props.forEach((value: unknown, key: string) => {
    // Drawer metadata controls root presentation and should not leak into the
    // dialog component's public props.
    if (key !== ROOT_MODAL_PRESENTATION_PROP && key !== ROOT_MODAL_DRAWER_PROP) {
      modalProps[key] = value;
    }
  });
  return modalProps;
};

const toPlainDrawerOptions = (value: unknown): RootModalDrawerOptions | null => {
  if (!value) {
    return null;
  }

  if (value === true) {
    return {};
  }

  if (isObject(value) && typeof (value as ImmutableLikeValue).toJS === 'function') {
    const plainValue = (value as ImmutableLikeValue).toJS?.();
    return isObject(plainValue) ? (plainValue as RootModalDrawerOptions) : null;
  }

  if (isObject(value)) {
    return value as RootModalDrawerOptions;
  }

  return null;
};

export const getRootModalDrawerOptions = (modal: RootModalRecord): RootModalDrawerOptions | null =>
  toPlainDrawerOptions(modal.props?.get?.(ROOT_MODAL_DRAWER_PROP));

const getDefaultRootModalDrawerOptions = (modal: RootModalRecord): RootModalDrawerOptions | null =>
  getRegisteredRootModalDrawerOptions(modal.type) ?? null;

export const getRootModalPresentation = (modal: RootModalRecord): RootModalPresentation =>
  modal.props?.get?.(ROOT_MODAL_PRESENTATION_PROP) ?? getRegisteredRootModalPresentation(modal.type);

export const getRootModalDrawerPresentationOptions = ({
  drawerEntries = [],
  hasUnderlyingModal = false,
  index,
  modalArray,
  webDrawerInput,
}: DrawerPresentationOptionsInput): RootModalDrawerPresentationOptions => {
  if (!webDrawerInput) {
    return {
      drawerOptions: null,
      plane: 'drawer',
      underlyingDrawerOptions: null,
    };
  }

  const underlyingDrawerOptions = drawerEntries[drawerEntries.length - 1] ?? null;
  const modal = modalArray[index];
  const drawerOptions = getRootModalDrawerOptions(modal) ?? getDefaultRootModalDrawerOptions(modal);

  return {
    drawerOptions,
    // RootModal's modal flag is monotonic while it walks the ordered records,
    // so every drawer after the first true modal stays on the modal plane.
    plane: hasUnderlyingModal ? 'modal' : 'drawer',
    underlyingDrawerOptions,
  };
};
